import { useContext, useState } from 'react';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Title } from '../components/ui/Title';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { useQuery } from '@tanstack/react-query';
import { UsersClient } from '../services/UsersClient';
import { UserContext } from '../context/UserProvider';
import { Skeleton } from '../components/ui/Skeleton';
import { Employee } from '../types/Employee';
import { Link } from '../components/ui/Link';
import { routeUrls } from '../routes';
import { Message } from '../components/ui/Message';

type FilterState = {
    name?: string;
    company?: string;
    afasId?: string;
};

export const UserManagementPage = () => {
    const [filterState, setFilterState] = useState<FilterState>({
        name: '',
        company: '',
        afasId: '',
    });
    const user = useContext(UserContext);
    const { data, isPending, isError } = useQuery({
        queryKey: ['users'],
        queryFn: () => UsersClient(user).getUsers(),
    });

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        setFilterState((prevState) => ({ ...prevState, [name]: value }));
    };

    const filterData = (data: Employee[]) => {
        return data.filter((employee) => {
            return (
                employee.name.toLowerCase().includes(filterState.name?.toLowerCase() || '') &&
                employee.company.toLowerCase().includes(filterState.company?.toLowerCase() || '') &&
                employee.afasId.toLowerCase().includes(filterState.afasId?.toLowerCase() || '')
            );
        });
    };

    return (
        <div className="flex flex-col gap-6 md:gap-12">
            <div className="py-3 md:py-0 flex justify-between items-center">
                <Title>Gebruikersbeheer</Title>
            </div>

            {isError && <Message type="error">Er is iets misgegaan bij het ophalen van de medewerkers. Probeer het later nog een keer.</Message>}

            {isPending && (
                <div className="flex w-full flex-col gap-2">
                    <div className="flex gap-6 mb-6">
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                        <Skeleton className="w-48 h-[40px] rounded-sm" />
                    </div>
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                    <Skeleton className="w-full h-[40px] rounded-sm" />
                </div>
            )}

            {data && (
                <>
                    <div className="flex flex-wrap gap-6">
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="employee" className="text-primary">
                                Medewerker
                            </Label>
                            <Input placeholder="Medewerker" className="w-48 text-sm" name="name" onChange={(e) => handleFilterChange(e)} />
                        </div>
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="company" className="text-primary">
                                Werkmaatschappij
                            </Label>
                            <Input placeholder="Werkmaatschappij" className="w-48 text-sm" name="company" onChange={(e) => handleFilterChange(e)} />
                        </div>
                        <div className="flex flex-col gap-3">
                            <Label htmlFor="afasId" className="text-primary">
                                AFAS ID
                            </Label>
                            <Input placeholder="AFAS ID" className="w-48 text-sm" name="afasId" onChange={(e) => handleFilterChange(e)} />
                        </div>
                    </div>

                    <div className="max-md:hidden w-full border-t-[1px] border-grey"></div>

                    <Table>
                        <TableHeader className="bg-secondary">
                            <TableRow>
                                <TableHead>Medewerker</TableHead>
                                <TableHead>Werkmaatschappij</TableHead>
                                <TableHead>AFAS ID</TableHead>
                                <TableHead>Bijgewerkt op</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data &&
                                filterData(data).map((employee, index) => {
                                    return (
                                        <TableRow className={`${(index + 1) % 2 === 0 ? 'bg-gray-100' : ''}`} key={index}>
                                            <TableCell>
                                                <Link href={routeUrls.DASHBOARD + `/${employee.id}`} className="underline text-sm" hideAnchorIcon>
                                                    {employee.name}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{employee.company}</TableCell>
                                            <TableCell>{employee.afasId}</TableCell>
                                            <TableCell>{employee.updatedAt}</TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </>
            )}
        </div>
    );
};
